import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Download, Upload, ExternalLink, FolderOpen, Printer, FileText } from "lucide-react";
import api, { errMsg, inr } from "../lib/api";
import PageHeader from "./PageHeader";
import StatusBadge from "./StatusBadge";
import SearchSelectAddNew from "./SearchSelectAddNew";
import OASearchSelect from "./OASearchSelect";
import { useAuth } from "../context/AuthContext";
import { REC_SCHEMAS } from "../config/recordSchemas";

const STATUS_LABEL = { pending: "Pending", in_progress: "In Progress", completed: "Completed", hold: "Hold" };

export default function OARecordModule({ recType, fixedOaId, fixedOaNumber, embedded }) {
  const schema = REC_SCHEMAS[recType];
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const can = (p) => isAdmin || (user.permissions || []).includes(p);
  const canView = can(`view_${recType}`);
  const canAdd = can(`add_${recType}`);
  const canEdit = can(`edit_${recType}`);
  const canDelete = can(`delete_${recType}`);

  const [rows, setRows] = useState([]);
  const [sp] = useSearchParams();
  const [filters, setFilters] = useState({ search: "", oaNumber: "", customer: "", status: sp.get("status") || "" });
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const fileRef = useRef();

  const fetchOa = async (num) => {
    if (!num) return null;
    try { const { data } = await api.get("/oas", { params: { oaNumber: num } }); return data.find((o) => o.oaNumber === num) || null; }
    catch { return null; }
  };
  const autofill = async (num, cur) => {
    const oa = await fetchOa(num);
    if (!oa) return;
    setForm((p) => {
      if (!p) return p;
      const data = { ...p.data };
      if (recType === "office_work" && !data.destination) data.destination = oa.destination || "";
      return { ...p, customerName: p.customerName || oa.customerName || "", data };
    });
  };

  const emptyForm = () => ({
    oaNumber: fixedOaNumber || "", customerName: "", status: schema.hasStatus ? "pending" : "",
    data: { ...(schema.masterField ? { [schema.masterField.key]: "" } : {}),
      ...Object.fromEntries((schema.fields || []).map((f) => [f.key, ""])),
      ...(schema.itemsKey ? { [schema.itemsKey]: [Object.fromEntries((schema.itemFields || []).map((f) => [f.key, ""]))] } : {}) },
  });

  const load = useCallback(async () => {
    if (!canView) return;
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    if (fixedOaId) params.oaId = fixedOaId;
    const { data } = await api.get(`/oa-records/${recType}`, { params });
    setRows(data);
  }, [recType, filters, fixedOaId, canView]);
  useEffect(() => { load(); }, [load]);

  const applyRec = (obj, f, rec) => {
    obj[f.key] = rec[f.valueField || "name"] ?? "";
    Object.entries(f.autofill || {}).forEach(([tgt, mk]) => { obj[tgt] = rec[mk] ?? ""; });
  };
  const selectData = (f) => (rec) => setForm((p) => {
    const data = { ...p.data }; applyRec(data, f, rec);
    const top = f.setsCustomer ? { customerName: rec.name } : {};
    return { ...p, ...top, data };
  });
  const selectItem = (i, f) => (rec) => setForm((p) => {
    const arr = [...(p.data[schema.itemsKey] || [])];
    const row = { ...arr[i] }; applyRec(row, f, rec); arr[i] = row;
    return { ...p, data: { ...p.data, [schema.itemsKey]: arr } };
  });

  const openNew = () => {
    setEditing(null); setForm(emptyForm()); setModal(true);
    if (fixedOaNumber) setTimeout(() => autofill(fixedOaNumber), 0);
    if (recType === "invoice") {
      const params = fixedOaNumber ? { oaNumber: fixedOaNumber } : {};
      api.get("/oa-records/invoice/next-srno", { params }).then(({ data }) => setForm((p) => (p ? { ...p, data: { ...p.data, srNo: String(data.next) } } : p))).catch(() => {});
    }
  };
  const openEdit = (r) => { setEditing(r); setForm({ oaNumber: r.oaNumber, customerName: r.customerName, status: r.status || "", data: { ...r.data } }); setModal(true); };
  const setData = (k, v) => setForm((p) => ({ ...p, data: { ...p.data, [k]: v } }));
  const setItem = (i, k, v) => setForm((p) => { const arr = [...(p.data[schema.itemsKey] || [])]; arr[i] = { ...arr[i], [k]: v }; return { ...p, data: { ...p.data, [schema.itemsKey]: arr } }; });
  const addItem = () => setForm((p) => ({ ...p, data: { ...p.data, [schema.itemsKey]: [...(p.data[schema.itemsKey] || []), Object.fromEntries((schema.itemFields || []).map((f) => [f.key, ""]))] } }));
  const removeItem = (i) => setForm((p) => ({ ...p, data: { ...p.data, [schema.itemsKey]: p.data[schema.itemsKey].filter((_, x) => x !== i) } }));

  const save = async () => {
    if (!form.oaNumber) return toast.error("OA Number is required.");
    for (const f of schema.fields || []) if (f.required && !form.data[f.key]) return toast.error(`${f.label} is required.`);
    try {
      const payload = { oaNumber: form.oaNumber, oaId: fixedOaId || "", customerName: form.customerName, status: form.status, data: form.data };
      if (editing) await api.put(`/oa-records/${recType}/${editing.id}`, payload);
      else await api.post(`/oa-records/${recType}`, payload);
      toast.success(editing ? "Updated" : "Added"); setModal(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (r) => { if (!window.confirm("Delete this record?")) return; try { await api.delete(`/oa-records/${recType}/${r.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); } };

  const uploadFile = async (r, field, f) => {
    if (!f) return;
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/oa-records/${recType}/${r.id}/file`, fd, { params: { field }, headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      setForm((p) => (p ? { ...p, files: [...(p.files || []), data] } : p));
      load();
    }
    catch (e) { toast.error(errMsg(e)); }
  };
  const openFile = (r, fileId, download) => {
    api.get(`/oa-records/${recType}/${r.id}/file/${fileId}`, { params: { mode: download ? "download" : "view" }, responseType: "blob" })
      .then(({ data }) => { const url = URL.createObjectURL(data); if (download) { const a = document.createElement("a"); a.href = url; a.download = "file"; a.click(); } else window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000); })
      .catch((e) => toast.error(errMsg(e)));
  };
  const exportData = (fmt) => {
    api.get(`/oa-records/${recType}/export/all`, { params: { format: fmt }, responseType: "blob" })
      .then(({ data, headers }) => { const cd = headers["content-disposition"] || ""; const m = cd.match(/filename="?([^"]+)"?/); const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = m ? m[1] : `${recType}.${fmt}`; a.click(); })
      .catch((e) => toast.error(errMsg(e)));
  };
  const printInvoice = (r) => {
    const w = window.open("", "_blank"); if (!w) return;
    const items = (r.data.items || []).map((it, i) => `<tr><td>${i + 1}</td><td>${it.description || ""}</td><td>${it.itemCode || ""}</td><td>${it.qty || 0}</td><td>${it.price || 0}</td><td>${it.amount || 0}</td></tr>`).join("");
    w.document.write(`<html><head><title>Invoice ${r.data.invoiceNo || ""}</title><style>body{font-family:Arial;padding:24px}table{border-collapse:collapse;width:100%}th,td{border:1px solid #000;padding:6px;text-align:left}h2{margin:0}</style></head><body><h2>INVOICE ${r.data.invoiceNo || ""}</h2><p>OA: ${r.oaNumber} · Customer: ${r.customerName} · Date: ${r.data.invoiceDate || ""}</p><table><thead><tr><th>Sr</th><th>Description</th><th>Code</th><th>Qty</th><th>Price</th><th>Amount</th></tr></thead><tbody>${items}</tbody></table><p style="text-align:right">Subtotal: ${r.data.subtotal || 0} &nbsp; GST (${r.data.gstPercent || 0}%): ${r.data.gstAmount || 0} &nbsp; <b>Total: ${r.data.totalAmount || 0}</b></p></body></html>`);
    w.document.close(); w.onload = () => w.print();
  };

  const cell = (r, c) => {
    if (c === "OA No.") return <span className="font-mono font-semibold">{r.oaNumber}</span>;
    if (c === "Customer") return r.customerName || "—";
    if (c === "Destination") return r.data.destination || "—";
    if (c === "Items") return (r.data.items || []).map((i) => `${i.item} (${i.qty})`).join(", ") || "—";
    if (c === "Invoice No.") return r.data.invoiceNo || "—";
    if (c === "Invoice Date") return r.data.invoiceDate || "—";
    if (c === "Total") return inr(r.data.totalAmount || 0);
    if (c === "Item") return r.data.item || r.data.description || "—";
    if (c === "Serial No.") return r.data.serialNo || "—";
    if (c === "Body No.") return r.data.bodyNo || "—";
    if (c === "Packing") return r.data.packing || "—";
    if (c === "Total Weight") return `${r.data.totalWeight || 0} kg`;
    if (c === "AURO-1 GP") return r.data.auro1GatePass || "—";
    if (c === "AURO-2 GP") return r.data.auro2GatePass || "—";
    if (c === "Vehicle No.") return <span className="flex items-center gap-1">{r.data.vehicleNo || r.data.localVehicleNo || "—"}{r.overdue && <span className="badge bg-red-50 text-red-700 border border-red-200 text-[10px]" data-testid={`overdue-${r.id}`}>⚠ Overdue</span>}</span>;
    if (c === "Transport Name") return r.data.transportName || "—";
    if (c === "LR No.") return r.data.lrNo || "—";
    if (c === "Team") return r.data.teamName || "—";
    if (c === "Status") return <span className="badge bg-slate-100 text-slate-700 border border-slate-200">{STATUS_LABEL[r.status] || r.status || "—"}</span>;
    return "—";
  };

  if (!canView) return <div className="p-8 text-slate-500">You do not have permission to view {schema.title}.</div>;

  const Table = (
    <div className="card overflow-hidden"><div className="overflow-x-auto"><table className="w-full">
      <thead className="bg-slate-50 border-b border-slate-200"><tr>
        {schema.columns.map((c) => <th key={c} className="th">{c}</th>)}
        <th className="th">Files</th><th className="th">Created By</th><th className="th">Actions</th>
      </tr></thead>
      <tbody className="divide-y divide-slate-100">
        {rows.length === 0 && <tr><td className="td text-slate-400" colSpan={schema.columns.length + 3}><div className="flex items-center gap-2 py-6 justify-center" data-testid={`${recType}-empty`}><FolderOpen className="h-5 w-5" /> {filters.oaNumber ? "No data found for this O.A. Number. Add new data." : "No records yet."}</div></td></tr>}
        {rows.map((r) => (
          <tr key={r.id} className="hover:bg-slate-50" data-testid={`${recType}-row-${r.id}`}>
            {schema.columns.map((c) => <td key={c} className="td">{cell(r, c)}</td>)}
            <td className="td">
              <div className="flex items-center gap-1">
                {(r.files || []).map((f) => <button key={f.fileId} title={f.filename} className="p-1 rounded hover:bg-slate-100" onClick={() => openFile(r, f.fileId, false)}><ExternalLink className="h-3.5 w-3.5 text-slate-500" /></button>)}
                {(schema.files || []).length > 0 && canEdit && <label className="p-1 rounded hover:bg-amber-50 cursor-pointer" title="Attach file"><Upload className="h-3.5 w-3.5 text-amber-600" /><input type="file" className="hidden" onChange={(e) => uploadFile(r, (schema.files[0] || {}).field || "attachment", e.target.files[0])} /></label>}
              </div>
            </td>
            <td className="td text-xs text-slate-400">{r.createdByName}</td>
            <td className="td"><div className="flex items-center gap-1">
              {schema.print && <button title="Print" className="p-1.5 rounded hover:bg-slate-100" onClick={() => printInvoice(r)} data-testid={`${recType}-print-${r.id}`}><Printer className="h-4 w-4 text-slate-600" /></button>}
              {canEdit && <button title="Edit" className="p-1.5 rounded hover:bg-slate-100" onClick={() => openEdit(r)} data-testid={`${recType}-edit-${r.id}`}><Pencil className="h-4 w-4 text-slate-600" /></button>}
              {canDelete && <button title="Delete" className="p-1.5 rounded hover:bg-red-50" onClick={() => del(r)} data-testid={`${recType}-delete-${r.id}`}><Trash2 className="h-4 w-4 text-red-600" /></button>}
            </div></td>
          </tr>
        ))}
      </tbody>
    </table></div></div>
  );

  const Modal = modal && form && (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setModal(false)}>
      <div className="card w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={`${recType}-modal`}>
        <div className="flex items-center justify-between mb-4"><h3 className="font-head text-xl font-bold">{editing ? "Edit" : (schema.addLabel || `Add ${schema.title}`)}{editing ? ` ${schema.title}` : ""}</h3><button onClick={() => setModal(false)}><X className="h-5 w-5 text-slate-400" /></button></div>
        <div className="grid md:grid-cols-2 gap-4">
          <div><label className="label">OA Number *</label><input className="input" value={form.oaNumber} onChange={(e) => setForm((p) => ({ ...p, oaNumber: e.target.value }))} onBlur={(e) => !fixedOaNumber && autofill(e.target.value)} disabled={!!fixedOaNumber} data-testid={`${recType}-oa-number`} /></div>
          <div><label className="label">Customer</label><input className="input" value={form.customerName} onChange={(e) => setForm((p) => ({ ...p, customerName: e.target.value }))} /></div>
          {schema.hasStatus && <div><label className="label">Status</label><select className="input" value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} data-testid={`${recType}-status`}>{schema.statusOptions.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}</select></div>}
          {schema.masterField && (
            <div><label className="label">{schema.masterField.label}</label>
              <SearchSelectAddNew master={schema.masterField.master} value={form.data[schema.masterField.key] || ""}
                onChange={(v) => setData(schema.masterField.key, v)} onSelect={selectData(schema.masterField)}
                testid={`${recType}-master-field`} />
            </div>
          )}
          {(schema.fields || []).map((f) => (
            <div key={f.key} className={f.full ? "md:col-span-2" : ""}>
              <label className="label">{f.label}{f.required ? " *" : ""}</label>
              {f.master ? (
                <SearchSelectAddNew master={f.master} value={form.data[f.key] ?? ""}
                  onChange={(v) => setData(f.key, v)} onSelect={selectData(f)}
                  testid={`${recType}-field-${f.key}`} />
              ) : (
                <input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} className="input" value={form.data[f.key] ?? ""} onChange={(e) => setData(f.key, e.target.value)} data-testid={`${recType}-field-${f.key}`} />
              )}
            </div>
          ))}
        </div>
        {schema.itemsKey && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2"><label className="label mb-0">Items</label><button className="btn-outline btn-sm" onClick={addItem}><Plus className="h-4 w-4" /> Add Item</button></div>
            <div className="space-y-2">
              {(form.data[schema.itemsKey] || []).map((it, i) => (
                <div key={i} className="grid gap-2" style={{ gridTemplateColumns: `repeat(${schema.itemFields.length}, 1fr) auto` }}>
                  {schema.itemFields.map((f) => (
                    f.master ? (
                      <SearchSelectAddNew key={f.key} master={f.master} value={it[f.key] ?? ""} placeholder={f.label}
                        onChange={(v) => setItem(i, f.key, v)} onSelect={selectItem(i, f)} testid={`${recType}-item-${i}-${f.key}`} />
                    ) : (
                      <input key={f.key} type={f.type === "number" ? "number" : "text"} placeholder={f.label} className="input" value={it[f.key] ?? ""} onChange={(e) => setItem(i, f.key, e.target.value)} data-testid={`${recType}-item-${i}-${f.key}`} />
                    )
                  ))}
                  <button className="text-red-600 p-1" onClick={() => removeItem(i)}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}
        {(schema.files || []).length > 0 && (
          <div className="mt-4 space-y-3">
            <label className="label mb-0">Documents &amp; Photos</label>
            {!editing && <p className="text-xs text-slate-400" data-testid={`${recType}-files-hint`}>Save the record first, then re-open it to attach files.</p>}
            {editing && schema.files.map((ff) => {
              const existing = (form.files || []).filter((x) => x.field === ff.field);
              return (
                <div key={ff.field} className="border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">{ff.label}</span>
                    {canEdit && <label className="btn-outline btn-sm cursor-pointer" data-testid={`${recType}-upload-${ff.field}`}><Upload className="h-4 w-4" /> Upload<input type="file" className="hidden" onChange={(e) => uploadFile(editing, ff.field, e.target.files[0])} /></label>}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {existing.length === 0 && <span className="text-xs text-slate-400">No file uploaded.</span>}
                    {existing.map((x) => (
                      <span key={x.fileId} className="flex items-center gap-1 text-xs border border-slate-200 rounded px-2 py-1">
                        <FileText className="h-3.5 w-3.5 text-slate-500" />
                        <button className="max-w-[140px] truncate" onClick={() => openFile(editing, x.fileId, false)} data-testid={`${recType}-file-view-${x.fileId}`}>{x.filename}</button>
                        <button onClick={() => openFile(editing, x.fileId, true)} title="Download" data-testid={`${recType}-file-dl-${x.fileId}`}><Download className="h-3 w-3 text-slate-500" /></button>
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-6">
          <button className="btn-outline" onClick={() => setModal(false)}>Cancel</button>
          <button className="btn-primary" onClick={save} data-testid={`${recType}-save`}>{editing ? "Save Changes" : "Add Record"}</button>
        </div>
      </div>
    </div>
  );

  if (embedded) return (<div className="space-y-3">{canAdd && <div className="flex justify-end"><button className="btn-accent btn-sm" onClick={openNew} data-testid={`${recType}-add-btn`}><Plus className="h-4 w-4" /> {schema.addLabel || `Add ${schema.title}`}</button></div>}{Table}{Modal}</div>);

  return (
    <div>
      <PageHeader title={schema.title} subtitle={`${rows.length} record(s)`} actions={
        <div className="flex gap-2">
          <button className="btn-outline btn-sm" onClick={() => exportData("excel")} data-testid={`${recType}-export-excel`}><Download className="h-4 w-4" /> Excel</button>
          <button className="btn-outline btn-sm" onClick={() => exportData("csv")}><Download className="h-4 w-4" /> CSV</button>
          {canAdd && <button className="btn-accent" onClick={openNew} data-testid={`${recType}-add-btn`}><Plus className="h-4 w-4" /> {schema.addLabel || `Add ${schema.title}`}</button>}
        </div>} />
      <div className="p-6 lg:p-8 space-y-4">
        <div className="card p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <input className="input" placeholder="Search" value={filters.search} onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))} data-testid={`${recType}-search`} />
          <OASearchSelect value={filters.oaNumber} onText={(v) => setFilters((p) => ({ ...p, oaNumber: v }))} onPick={(o) => setFilters((p) => ({ ...p, oaNumber: o.oaNumber }))} placeholder="Search / Select O.A. Number" testid={`${recType}-oa-filter`} />
          <input className="input" placeholder="Customer" value={filters.customer} onChange={(e) => setFilters((p) => ({ ...p, customer: e.target.value }))} />
          {schema.hasStatus && <select className="input" value={filters.status} onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}><option value="">All Status</option>{schema.statusOptions.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}</select>}
        </div>
        {Table}
      </div>
      {Modal}
    </div>
  );
}
